// =============================================================================
// ui/shell/tabs.ts — the tab registry, as DATA.
// -----------------------------------------------------------------------------
// Relocated from the old client's `TABS` array (app/index.html) with the ids,
// labels, order and grouping unchanged. "Relocate, do not redesign" —
// REBUILD_PLAN §7 — so this is deliberately not an improvement on the original.
//
// It is a plain module with no React in it so the navigation RULES are testable
// without rendering anything. The rules are small and every one of them exists
// because the old client got it wrong at least once:
//
//   a hidden tab must not be reachable, including as a saved "last tab"
//   a Pro-gated tab is REACHABLE and shows an upgrade panel — never hidden
//   analytics sub-tabs share one section and differ only by `view`
//
// ⚠ Routing stays in React state, matching the old client, which has no URL
// route for the active tab. Hash routing would be a real improvement — refresh
// and the back button would both work — but it is a behaviour change during a
// phase whose gate is parity, so it is a separate decision for the D-log rather
// than something to slip in here.
// =============================================================================

/** Sidebar grouping. Numeric groups render in order; the named ones are sections. */
export type TabGroup = 1 | 2 | 3 | 'analytics' | 'reports' | 'lab' | 'bottom';

export interface TabDef {
  id: string;
  label: string;
  icon: string;
  group: TabGroup;
  /** Pro-gated. Reachable when not entitled — it renders an upgrade panel. */
  gate?: 'pro';
  /** Analytics sub-tabs share one section and select a view within it. */
  view?: string;
  /** Renders a section heading above this item in the sidebar. */
  sectionLabel?: string;
}

export const TABS: readonly TabDef[] = [
  // ⚠ `TRADING JOURNAL` was missing until the shell's parity pass — the registry
  // carried the ANALYTICS and REPORTS headings but not the first one, so the top
  // group rendered unlabelled while the others were titled.
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', group: 1, sectionLabel: 'TRADING JOURNAL' },
  { id: 'journal', label: 'Journal', icon: 'journal', group: 1 },
  { id: 'trades', label: 'Trades', icon: 'trades', group: 1 },
  { id: 'calendar', label: 'Calendar', icon: 'calendar', group: 1 },
  { id: 'dayview', label: 'Day Overview', icon: 'calendar', group: 1, gate: 'pro' },

  { id: 'analytics-overview', label: 'Overview', icon: 'analytics', group: 'analytics', view: 'overview', sectionLabel: 'ANALYTICS', gate: 'pro' },
  { id: 'analytics-setup', label: 'Setup Quality', icon: 'patterns', group: 'analytics', view: 'setup', gate: 'pro' },
  { id: 'analytics-excursion', label: 'Excursion', icon: 'analytics', group: 'analytics', view: 'excursion', gate: 'pro' },
  { id: 'analytics-prop', label: 'Prop Report', icon: 'flag', group: 'analytics', view: 'prop', gate: 'pro' },
  { id: 'analytics-custom', label: 'Custom Analytics', icon: 'analytics', group: 'analytics', view: 'custom', gate: 'pro' },

  { id: 'reports', label: "Coach's Report", icon: 'report', group: 'reports', sectionLabel: 'REPORTS' },
  // 🚨 ON A LICENCE HOLD, NOT MERELY UNPORTED. TRADECOACH has no market-data
  // redistribution licence, and the daily brief was measured publishing a live
  // quote (D170). Josh's call is to stop presenting it anywhere until what is
  // permitted is clear, so this tab is REMOVED from the registry rather than
  // left to render "Not ported yet" — a placeholder still advertises a feature.
  //
  // ⚠ The real gate is RLS on `market_reports`, which now admits the owner's
  // login only. Removing the entry stops this client offering a door; it is not
  // what keeps the data in. Restore this line when the licence question is
  // answered — the port work is D171's open item, not lost.
  //
  // ✅ AND THE BUY SCREEN NO LONGER PROMISES IT. It did — the comparison table
  // carried a free "Market Intel ✓" row while this line stayed commented out,
  // so the plan sold a tab the app had deliberately removed (#90). The row came
  // off with Josh's call. ▶ Restoring this line means restoring that row too.
  // { id: 'macro', label: 'Market Intel', icon: 'report', group: 'reports' },

  { id: 'accounts', label: 'Accounts', icon: 'accounts', group: 2 },
  { id: 'patterns', label: 'Patterns', icon: 'patterns', group: 2 },

  // ⚠ `EDGE DEVELOPMENT` is Josh's addition, not a relocation — the old client
  // leaves this group unlabelled. Recorded as an addition so the next parity
  // comparison does not read it as a divergence and "fix" it back out.
  { id: 'backtests', label: 'Backtesting', icon: 'backtests', group: 'lab', gate: 'pro', sectionLabel: 'EDGE DEVELOPMENT' },
  { id: 'backtest-dashboard', label: 'Results', icon: 'btdash', group: 'lab', gate: 'pro' },
  { id: 'practice', label: 'Practice', icon: 'backtests', group: 'lab', gate: 'pro' },

  { id: 'review', label: 'Review', icon: 'review', group: 3 },
  { id: 'bestopps', label: 'Best Opps', icon: 'star', group: 3 },

  { id: 'account', label: 'My Account', icon: 'user', group: 'bottom' },
  { id: 'settings', label: 'Settings', icon: 'settings', group: 'bottom' },
];

/** Where an unreachable request lands. Present in every configuration. */
export const FALLBACK_TAB = 'dashboard';

export const tabById = (id: string): TabDef | undefined => TABS.find((t) => t.id === id);

/**
 * The DOM section an id renders into.
 *
 * Analytics sub-tabs all share `analytics`. The old client normalised this at
 * the `renderTab` boundary rather than at each of 14+ call sites, after the
 * global filter appeared broken on every Excursion / Overview / Prop screen
 * because those callers passed the sub-tab id straight through.
 */
export const sectionFor = (id: string): string =>
  id.startsWith('analytics-') ? 'analytics' : id;

/**
 * Is the SECTION this id renders into behind the paywall?
 *
 * ── ⚠ WHY A SECTION AND NOT A TAB ──
 *
 * A section is gated only when EVERY registry entry mapping to it is gated. The
 * five `analytics-*` ids share one section, so ungating a single sub-tab later
 * would ungate the section — which is exactly what the old client does, because
 * over there one renderer paints all five.
 *
 * This client renders each sub-tab separately and COULD gate them one by one.
 * It deliberately does not: while all five carry the gate the two rules agree,
 * and the first time they disagree is the first time the two clients would show
 * a different screen for the same account. Parity is the gate for this phase.
 *
 * ⚠ Takes no view of entitlement. This answers "is it gated", never "may this
 * person see it" — the caller combines it with `useEntitlement`.
 */
export function isSectionProGated(id: string): boolean {
  const section = sectionFor(id);
  const entries = TABS.filter((t) => sectionFor(t.id) === section);
  // ⚠ `entries.length > 0` matters: `every` on an empty list is TRUE, so an
  // unknown id would otherwise report itself as gated and paywall the fallback.
  return entries.length > 0 && entries.every((t) => t.gate === 'pro');
}

export interface NavContext {
  /**
   * `hiddenTabs` from preferences.
   *
   * ⚠ `| undefined` is required, not noise: tsconfig sets
   * `exactOptionalPropertyTypes`, so a caller passing `{ hidden }` where
   * `hidden` is itself optional is a type error without it — and every caller
   * here forwards an optional prop.
   */
  hidden?: readonly string[] | undefined;
}

/**
 * Resolve a requested tab to the one that should actually open.
 *
 * ⚠ Pro gating is NOT resolved here, deliberately. A gated tab stays reachable
 * and renders an upgrade panel — that panel is the entire upsell surface, and
 * redirecting away from it would delete the product's own sales pitch while
 * looking like a tidy guard. The old client made the same call: its `navigate()`
 * contains no Pro check at all, and the gate is applied where the tab is
 * RENDERED. See `isSectionProGated` below and `ui/shell/ProUpgrade.tsx`.
 *
 * ⚠ This paragraph used to end "…and the free-tier suite pins it with 132
 * assertions", which invited the reader to believe THIS client was covered by
 * it. The suite is `scripts/free-tier-caps-test.cjs` in the OLD repo and it
 * reads the old file's text; it says nothing about this one. Measured: 132
 * assertions, passing, on 2026-09-03.
 */
export function resolveTab(requested: string | null | undefined, ctx: NavContext = {}): string {
  const id = String(requested ?? '');
  const tab = tabById(id);
  if (!tab) return FALLBACK_TAB;
  // A hidden tab is unreachable even when it is the saved "last tab" — which is
  // exactly how the old client could restore you onto a tab you had hidden.
  if (ctx.hidden?.includes(id)) return FALLBACK_TAB;
  return id;
}

/** Sidebar order, grouped, with hidden tabs removed. */
export const GROUP_ORDER: readonly TabGroup[] = [1, 'analytics', 'reports', 2, 'lab', 3, 'bottom'];

export function visibleGroups(ctx: NavContext = {}): { group: TabGroup; tabs: TabDef[] }[] {
  const hidden = new Set(ctx.hidden ?? []);
  return GROUP_ORDER
    .map((group) => ({ group, tabs: TABS.filter((t) => t.group === group && !hidden.has(t.id)) }))
    // An empty group must not render its separator or section heading.
    .filter((g) => g.tabs.length > 0);
}
